import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserStore } from '../stores/useUserStore';
import { useTranslation } from '../hooks/useTranslation';
import {
  User,
  Clock,
  Calendar,
  ArrowLeftRight,
  CheckSquare,
  Star,
  Trophy,
  TrendingUp,
  Star as RateReview,
  Gift,
  Settings,
  Target,
  MoreHorizontal,
  Pin,
  PinOff,
  FileText,
  DollarSign,
} from 'lucide-react';

// Import shadcn/ui components
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';

// Import existing components - only working ones for now
import ModernStaffProfileList from './ModernStaffProfileList';
import ModernCheckinCheckout from './ModernCheckinCheckout';
import ModernAttendanceRecords from './ModernAttendanceRecords';
import ModernPointEarning from './ModernPointEarning';
import ModernStaffBadgeList from './ModernStaffBadgeList';
import ModernStaffRanking from './ModernStaffRanking';
import ModernStaffRating from './ModernStaffRating';
import ModernPointRedemption from './ModernPointRedemption';
import ModernShiftSwitching from './ModernShiftSwitching';
import ModernSchedule from './ModernSchedule';
import ModernDailyChecklist from './ModernDailyChecklist';
import ModernPointStore from './ModernPointStore';
import ModernLeaveRequests from './ModernLeaveRequests';
import ModernPolicyManagement from '../policies/ModernPolicyManagement';

// Tab configuration matching STAFF_FEATURES.md exactly
// This will be converted to a function that uses translations
const getStaffTabs = (translate: (key: string) => string) => [
  // 1. My Profile
  {
    id: 'profile',
    label: translate('pos.staff.tabs.my_profile'),
    icon: User,
    component: ModernStaffProfileList,
    dataTestId: 'profile-tab',
    description: translate('pos.staff.tabs.my_profile_desc'),
  },
  // 2. Check-in/Check-out
  {
    id: 'checkin',
    label: translate('pos.staff.tabs.checkin_checkout'),
    icon: Clock,
    component: ModernCheckinCheckout,
    dataTestId: 'checkin-tab',
    description: translate('pos.staff.tabs.checkin_checkout_desc'),
  },
  // 3. View Attendance Record (Timesheet)
  {
    id: 'attendance',
    label: translate('pos.staff.tabs.attendance'),
    icon: Calendar,
    component: ModernAttendanceRecords,
    dataTestId: 'attendance-tab',
    description: translate('pos.staff.tabs.attendance_desc'),
  },
  // 4. View Working Schedule
  {
    id: 'schedule',
    label: translate('pos.staff.tabs.schedule'),
    icon: Target,
    component: ModernSchedule,
    dataTestId: 'schedule-tab',
    description: translate('pos.staff.tabs.schedule_desc'),
  },
  // 5. Negotiate Shift Switching
  {
    id: 'shift-switching',
    label: translate('pos.staff.tabs.shift_switching'),
    icon: ArrowLeftRight,
    component: ModernShiftSwitching,
    dataTestId: 'shift-switching-tab',
    description: translate('pos.staff.tabs.shift_switching_desc'),
  },
  // 6. View Checklist (Daily Checklist)
  {
    id: 'checklist',
    label: translate('pos.staff.tabs.daily_checklist'),
    icon: CheckSquare,
    component: ModernDailyChecklist,
    dataTestId: 'checklist-tab',
    description: translate('pos.staff.tabs.daily_checklist_desc'),
  },
  // 7. Leave Requests
  {
    id: 'leave-requests',
    label: translate('pos.staff.tabs.leave_requests'),
    icon: Calendar,
    component: ModernLeaveRequests,
    dataTestId: 'leave-requests-tab',
    description: translate('pos.staff.tabs.leave_requests_desc'),
  },
  // 8. Point Earning System
  {
    id: 'points',
    label: translate('pos.staff.tabs.point_earning'),
    icon: Star,
    component: ModernPointEarning,
    dataTestId: 'points-tab',
    description: translate('pos.staff.tabs.point_earning_desc'),
  },
  // 9. Point Store
  {
    id: 'point-store',
    label: translate('pos.staff.tabs.point_store'),
    icon: Gift,
    component: ModernPointStore,
    dataTestId: 'point-store-tab',
    description: translate('pos.staff.tabs.point_store_desc'),
  },
  // 10. Point Redemption
  {
    id: 'redemption',
    label: translate('pos.staff.tabs.redemption'),
    icon: Gift,
    component: ModernPointRedemption,
    dataTestId: 'redemption-tab',
    description: translate('pos.staff.tabs.redemption_desc'),
  },
  // 11. Ranking Board
  {
    id: 'ranking',
    label: translate('pos.staff.tabs.ranking'),
    icon: TrendingUp,
    component: ModernStaffRanking,
    dataTestId: 'ranking-tab',
    description: translate('pos.staff.tabs.ranking_desc'),
  },
  // 12. Reward Badges
  {
    id: 'badges',
    label: translate('pos.staff.tabs.badges'),
    icon: Trophy,
    component: ModernStaffBadgeList,
    dataTestId: 'badges-tab',
    description: translate('pos.staff.tabs.badges_desc'),
  },
  // 13. Staff Rating System
  {
    id: 'rating',
    label: translate('pos.staff.tabs.rating'),
    icon: RateReview,
    component: ModernStaffRating,
    dataTestId: 'rating-tab',
    description: translate('pos.staff.tabs.rating_desc'),
  },
  // 14. Policies (Read-only for staff)
  {
    id: 'policies',
    label: translate('pos.staff.tabs.policies'),
    icon: FileText,
    component: ModernPolicyManagement,
    dataTestId: 'policies-tab',
    description: translate('pos.staff.tabs.policies_desc'),
  },
];

// Tab usage tracking and management
interface TabUsage {
  id: string;
  count: number;
  lastUsed: number;
  pinned: boolean;
}

const STORAGE_KEY = 'neko-tab-usage';

const ModernStaffFeatures: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [tabUsage, setTabUsage] = useState<Record<string, TabUsage>>({});
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const { user } = useUserStore();
  const { translate, locale } = useTranslation();

  // Get staff tabs with translations
  const staffTabs = useMemo(() => getStaffTabs(translate), [translate, locale]);

  // Load tab usage from localStorage
  useEffect(() => {
    const savedUsage = localStorage.getItem(STORAGE_KEY);
    if (savedUsage) {
      try {
        setTabUsage(JSON.parse(savedUsage));
      } catch (error) {
        console.error('Error loading tab usage:', error);
      }
    }
  }, []);

  // Save tab usage to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tabUsage));
  }, [tabUsage]);

  // Track tab usage
  const trackTabUsage = (tabId: string) => {
    setTabUsage((prev) => {
      const current = prev[tabId] || {
        id: tabId,
        count: 0,
        lastUsed: 0,
        pinned: false,
      };
      return {
        ...prev,
        [tabId]: {
          ...current,
          count: current.count + 1,
          lastUsed: Date.now(),
        },
      };
    });
  };

  // Toggle pin status
  const togglePin = (tabId: string) => {
    setTabUsage((prev) => ({
      ...prev,
      [tabId]: {
        ...prev[tabId],
        pinned: !prev[tabId]?.pinned,
      },
    }));
  };

  // Handle tab change
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    trackTabUsage(tabId);
    // Update URL with query parameter to retain tab state
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', tabId);
    navigate({ search: searchParams.toString() }, { replace: true });
  };

  // Handle query parameter navigation and custom events for tab switching
  useEffect(() => {
    // Read tab from query parameters
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');

    if (tabParam && staffTabs.some((tab) => tab.id === tabParam)) {
      setActiveTab(tabParam);
      trackTabUsage(tabParam);
    }

    // Listen for custom navigation events (from notifications)
    const handleNavigateToTab = (event: CustomEvent) => {
      const tabId = event.detail?.tabId;
      if (tabId && staffTabs.some((tab) => tab.id === tabId)) {
        setActiveTab(tabId);
        trackTabUsage(tabId);
        // Update URL with query parameter
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.set('tab', tabId);
        navigate({ search: newSearchParams.toString() }, { replace: true });
      }
    };

    window.addEventListener(
      'navigateToTab',
      handleNavigateToTab as EventListener
    );

    return () => {
      window.removeEventListener(
        'navigateToTab',
        handleNavigateToTab as EventListener
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, staffTabs, navigate]);

  // Filter tabs based on user roles
  const getFilteredTabs = () => {
    return staffTabs.filter((tab) => {
      // Shift Switching is only for Store Manager and Org Admin
      if (tab.id === 'shift-switching') {
        return (
          user?.roles?.includes('storeManager') ||
          user?.roles?.includes('orgAdmin')
        );
      }

      // All other tabs are visible to all staff
      return true;
    });
  };

  // Get top 5 most used tabs (including pinned tabs)
  const getTopTabs = () => {
    const filteredTabs = getFilteredTabs();

    // Get pinned tabs first
    const pinnedTabs = filteredTabs.filter((tab) => tabUsage[tab.id]?.pinned);

    // Get remaining tabs sorted by usage
    const remainingTabs = filteredTabs
      .filter((tab) => !tabUsage[tab.id]?.pinned)
      .sort((a, b) => {
        const aUsage = tabUsage[a.id] || { count: 0, lastUsed: 0 };
        const bUsage = tabUsage[b.id] || { count: 0, lastUsed: 0 };

        // Sort by count first, then by last used
        if (aUsage.count !== bUsage.count) {
          return bUsage.count - aUsage.count;
        }
        return bUsage.lastUsed - aUsage.lastUsed;
      });

    // Combine pinned tabs with top remaining tabs
    const topTabs = [...pinnedTabs];
    const remainingSlots = 5 - pinnedTabs.length;

    for (let i = 0; i < remainingSlots && i < remainingTabs.length; i++) {
      topTabs.push(remainingTabs[i]);
    }

    return topTabs;
  };

  // Get remaining tabs (not in top 5)
  const getRemainingTabs = () => {
    const filteredTabs = getFilteredTabs();
    const topTabs = getTopTabs();
    const topTabIds = topTabs.map((tab) => tab.id);

    return filteredTabs.filter((tab) => !topTabIds.includes(tab.id));
  };

  const filteredTabs = getFilteredTabs();
  const topTabs = getTopTabs();
  const remainingTabs = getRemainingTabs();

  return (
    <div className="w-full py-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-4xl font-bold tracking-tight">
          {translate('pos.staff.header.title')}
        </h1>
        <p className="text-muted-foreground">
          {translate('pos.staff.header.subtitle')}
        </p>
      </div>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <div className="w-full overflow-x-auto">
          <TabsList className="inline-flex w-max min-w-full gap-1">
            {/* Top 5 tabs */}
            {topTabs.map((tab) => {
              const IconComponent = tab.icon;
              const isPinned = tabUsage[tab.id]?.pinned;
              return (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  data-testid={tab.dataTestId}
                  className="flex items-center gap-1 text-xs sm:text-sm relative group"
                  title={tab.description}
                >
                  <IconComponent className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden lg:inline">{tab.label}</span>
                  <span className="lg:hidden">{tab.label.split(' ')[0]}</span>
                </TabsTrigger>
              );
            })}

            {/* More button for remaining tabs */}
            {remainingTabs.length > 0 && (
              <DropdownMenu open={showMoreMenu} onOpenChange={setShowMoreMenu}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center gap-1 text-xs sm:text-sm"
                  >
                    <MoreHorizontal className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden lg:inline">
                      {translate('pos.staff.tabs.more')}
                    </span>
                    <span className="lg:hidden">⋯</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {remainingTabs.map((tab) => {
                    const IconComponent = tab.icon;
                    const isPinned = tabUsage[tab.id]?.pinned;
                    return (
                      <DropdownMenuItem
                        key={tab.id}
                        onClick={() => {
                          handleTabChange(tab.id);
                          setShowMoreMenu(false);
                        }}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <IconComponent className="h-4 w-4" />
                          <span>{tab.label}</span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePin(tab.id);
                          }}
                          className="ml-2"
                          title={
                            isPinned
                              ? translate('pos.staff.pin.unpin_tab')
                              : translate('pos.staff.pin.pin_tab')
                          }
                        >
                          {isPinned ? (
                            <Pin className="h-3 w-3 text-blue-500" />
                          ) : (
                            <PinOff className="h-3 w-3 text-gray-400" />
                          )}
                        </button>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </TabsList>
        </div>

        {/* Tab Content */}
        {filteredTabs.map((tab) => {
          const Component = tab.component;
          const isPinned = tabUsage[tab.id]?.pinned;
          return (
            <TabsContent key={tab.id} value={tab.id} className="mt-6">
              <div className="relative">
                {/* Pin Button Overlay */}
                <div className="absolute top-0 right-0 z-10">
                  <button
                    onClick={() => togglePin(tab.id)}
                    className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/90 hover:bg-white shadow-sm border transition-colors"
                    title={
                      isPinned
                        ? translate('pos.staff.pin.unpin_tab')
                        : translate('pos.staff.pin.pin_tab')
                    }
                  >
                    {isPinned ? (
                      <Pin className="h-4 w-4 text-blue-500" />
                    ) : (
                      <PinOff className="h-4 w-4 text-gray-400" />
                    )}
                    <span className="text-sm text-muted-foreground">
                      {isPinned
                        ? translate('pos.staff.pin.pinned')
                        : translate('pos.staff.pin.pin')}
                    </span>
                  </button>
                </div>
                <Component />
              </div>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
};

export default ModernStaffFeatures;
